import { useState, useEffect } from 'react';
import { Plus, Search, UserCheck, UserX, Phone, Mail, Shield, Trash2, Edit2, Building } from 'lucide-react';
import { getUsers, createUser, updateUser, deactivateUser, activateUser, deleteUser, setUserPrimaryBranch } from '../api/users';
import { listBranches } from '../api/branches';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/Button';
import { Input, Select } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Badge } from '../components/ui/Badge';
import { ROLE_LABELS } from '../utils/constants';

function timeAgo(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'Az önce';
  if (min < 60) return `${min} dk önce`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} saat önce`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} gün önce`;
  return `${Math.floor(d / 30)} ay önce`;
}

const ROLE_COLORS = {
  admin:       'bg-purple-100 text-purple-700',
  supervisor:  'bg-blue-100 text-blue-700',
  field_staff: 'bg-green-100 text-green-700',
};

function UserForm({ onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    name: '', email: '', password: '', role: 'field_staff', phone: '',
  });

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const validatePhone = (phone) => {
    if (!phone) return null;
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) return 'Telefon 10 haneli, 5 ile başlamalı (örn: 5xx xxx xx xx)';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = 'Ad Soyad gerekli';
    if (!form.email.trim()) errs.email = 'E-posta gerekli';
    if (!form.password || form.password.length < 6) errs.password = 'En az 6 karakter';
    const phoneErr = validatePhone(form.phone);
    if (phoneErr) errs.phone = phoneErr;
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setLoading(true);
    try {
      await createUser(form);
      onSuccess();
    } catch (err) {
      const data = err.response?.data;
      const serverErrors = {};
      if (data?.errors) {
        data.errors.forEach((e) => {
          const field = e.path || e.param;
          if (field) serverErrors[field] = e.msg;
        });
      }
      if (Object.keys(serverErrors).length) {
        setErrors(serverErrors);
      } else {
        setErrors({ submit: data?.error || 'Hata oluştu' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input label="Ad Soyad *" value={form.name} onChange={set('name')} error={errors.name} />
      <Input label="E-posta *" type="email" value={form.email} onChange={set('email')} error={errors.email} />
      <Input label="Şifre *" type="password" value={form.password} onChange={set('password')} error={errors.password} placeholder="En az 6 karakter" />
      <Input label="Telefon" value={form.phone} onChange={set('phone')} error={errors.phone} placeholder="5xx xxx xx xx" />
      <Select label="Rol" value={form.role} onChange={set('role')}>
        <option value="field_staff">Saha Personeli</option>
        <option value="supervisor">Sorumlu</option>
        <option value="admin">Yönetici</option>
      </Select>
      {errors.submit && <p className="text-sm text-red-500 bg-red-50 rounded-lg px-3 py-2">{errors.submit}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel}>İptal</Button>
        <Button type="submit" loading={loading}>Kaydet</Button>
      </div>
    </form>
  );
}

function UserEditForm({ user, branches, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    name:              user.name              || '',
    email:             user.email             || '',
    phone:             user.phone             || '',
    role:              user.role              || 'field_staff',
    password:          '',
    is_active:         user.is_active,
    primary_branch_id: user.primary_branch_id || '',
  });

  const set = (k) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [k]: val }));
  };

  const validatePhone = (phone) => {
    if (!phone) return null;
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) return 'Telefon 10 haneli, 5 ile başlamalı (örn: 5xx xxx xx xx)';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim())  errs.name  = 'Ad Soyad gerekli';
    if (!form.email.trim()) errs.email = 'E-posta gerekli';
    if (form.password && form.password.length < 6) errs.password = 'En az 6 karakter';
    const phoneErr = validatePhone(form.phone);
    if (phoneErr) errs.phone = phoneErr;
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setLoading(true);
    try {
      const payload = {
        name:      form.name,
        email:     form.email,
        phone:     form.phone || null,
        role:      form.role,
        is_active: form.is_active,
      };
      if (form.password) payload.password = form.password;
      await updateUser(user.id, payload);

      // Sprint 2.8.5 T6-C: primary_branch_id ayrı endpoint (PATCH /:id/primary-branch, T2).
      // Yalnız değişiklikte çağır (gereksiz istek atma).
      const newBranchId = form.primary_branch_id || null;
      const oldBranchId = user.primary_branch_id || null;
      if (newBranchId !== oldBranchId) {
        await setUserPrimaryBranch(user.id, newBranchId);
      }

      onSuccess();
    } catch (err) {
      const data = err.response?.data;
      const serverErrors = {};
      if (data?.errors) {
        data.errors.forEach((e) => {
          const field = e.path || e.param;
          if (field) serverErrors[field] = e.msg;
        });
      }
      if (Object.keys(serverErrors).length) {
        setErrors(serverErrors);
      } else {
        setErrors({ submit: data?.error || 'Hata oluştu' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input label="Ad Soyad *" value={form.name} onChange={set('name')} error={errors.name} />
      <Input label="E-posta *" type="email" value={form.email} onChange={set('email')} error={errors.email} />
      <Input label="Telefon" value={form.phone} onChange={set('phone')} error={errors.phone} placeholder="5xx xxx xx xx" />
      <Select label="Rol" value={form.role} onChange={set('role')}>
        <option value="field_staff">Saha Personeli</option>
        <option value="supervisor">Sorumlu</option>
        <option value="admin">Yönetici</option>
      </Select>
      <Select label="Birincil Şube" value={form.primary_branch_id} onChange={set('primary_branch_id')}>
        <option value="">Şube atanmamış</option>
        {branches.map(b => (
          <option key={b.id} value={b.id}>{b.name}</option>
        ))}
      </Select>
      <Input
        label="Yeni Şifre"
        type="password"
        value={form.password}
        onChange={set('password')}
        error={errors.password}
        placeholder="Değiştirmek istemiyorsanız boş bırakın"
      />
      <div className="flex items-center gap-2">
        <input
          id="edit-is-active"
          type="checkbox"
          checked={form.is_active}
          onChange={set('is_active')}
          className="w-4 h-4 rounded border-slate-300 text-blue-600"
        />
        <label htmlFor="edit-is-active" className="text-sm text-slate-700 cursor-pointer">Hesap aktif</label>
      </div>
      {errors.submit && <p className="text-sm text-red-500 bg-red-50 rounded-lg px-3 py-2">{errors.submit}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel}>İptal</Button>
        <Button type="submit" loading={loading}>Kaydet</Button>
      </div>
    </form>
  );
}

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [branches, setBranches] = useState([]);

  const load = () => {
    setLoading(true);
    getUsers().then(d => setUsers(d.users)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // Sprint 2.8.5 T6-C: aktif şube listesi (mount'ta bir kez — modal her açılışta değil).
    // Hata: console.error, modal yine açılır (Şube Select boş gelir, atama bloke olmaz).
    listBranches({ is_active: true })
      .then(d => setBranches(d.branches || []))
      .catch(err => console.error('Şube listesi yüklenemedi:', err));
  }, []);

  const filtered = users.filter(u => {
    const matchSearch = !search ||
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase());
    const matchRole = !roleFilter || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  const handleDeactivate = async (user) => {
    if (!window.confirm(`${user.name} hesabını devre dışı bırakmak istediğinizden emin misiniz?`)) return;
    try {
      await deactivateUser(user.id);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'İşlem başarısız');
    }
  };

  const handleActivate = async (user) => {
    try {
      await activateUser(user.id);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'İşlem başarısız');
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`${user.name} kullanıcısını kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) return;
    try {
      await deleteUser(user.id);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Kullanıcı silinemedi');
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Personel</h1>
          <p className="text-slate-500 text-sm mt-0.5">{users.length} kullanıcı</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus size={16} />
          Yeni Personel
        </Button>
      </div>

      {/* Arama + Filtre */}
      <div className="flex gap-3 mb-5">
        <div className="flex-1 relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="İsim veya e-posta ara..."
            className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <Select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="w-44">
          <option value="">Tüm Roller</option>
          <option value="field_staff">Saha Personeli</option>
          <option value="supervisor">Sorumlu</option>
          <option value="admin">Yönetici</option>
        </Select>
      </div>

      {/* Tablo */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Kullanıcı</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">İletişim</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Rol</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Durum</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Son Giriş</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map(user => (
                <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm shrink-0">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <p className="font-medium text-slate-800 text-sm">{user.name}</p>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1.5 text-xs text-slate-500">
                        <Mail size={11} /> {user.email}
                      </div>
                      {user.phone && (
                        <div className="flex items-center gap-1.5 text-xs text-slate-500">
                          <Phone size={11} /> {user.phone}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <Badge className={ROLE_COLORS[user.role]}>
                      <Shield size={10} />
                      {ROLE_LABELS[user.role]}
                    </Badge>
                    {user.primary_branch_id && (
                      <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1">
                        <Building size={10} />
                        {user.primary_branch_name || (
                          <span className="italic text-amber-600">Şube silinmiş</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className={`inline-flex items-center gap-1 text-xs font-medium ${user.is_active ? 'text-green-600' : 'text-slate-400'}`}>
                      {user.is_active ? <UserCheck size={13} /> : <UserX size={13} />}
                      {user.is_active ? 'Aktif' : 'Pasif'}
                    </div>
                    {user.role === 'field_staff' && user.availability_status && user.availability_status !== 'active' && (
                      <div className={`inline-flex items-center gap-1 text-[10px] font-semibold mt-1 px-1.5 py-0.5 rounded-full ${
                        user.availability_status === 'break' ? 'bg-amber-100 text-amber-700' :
                        user.availability_status === 'lunch' ? 'bg-orange-100 text-orange-700' :
                        'bg-red-100 text-red-600'
                      }`}>
                        {user.availability_status === 'break' ? '🟡 Mola' :
                         user.availability_status === 'lunch' ? '🍽️ Yemekte' : '🔴 Müsait Değil'}
                        {user.availability_since && ` · ${Math.floor((Date.now() - new Date(user.availability_since).getTime()) / 60000)} dk`}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-xs text-slate-500">
                      {user.last_login_at ? timeAgo(user.last_login_at) : 'Henüz giriş yok'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setEditUser(user)}>
                        <Edit2 size={13} /> Düzenle
                      </Button>
                      {user.id !== currentUser?.id && user.is_active && (
                        <Button variant="ghost" size="sm" onClick={() => handleDeactivate(user)} className="text-orange-500 hover:text-orange-700 hover:bg-orange-50">
                          Devre Dışı
                        </Button>
                      )}
                      {user.id !== currentUser?.id && !user.is_active && (
                        <Button variant="ghost" size="sm" onClick={() => handleActivate(user)} className="text-green-600 hover:text-green-800 hover:bg-green-50">
                          <UserCheck size={13} /> Aktive Et
                        </Button>
                      )}
                      {user.id !== currentUser?.id && (
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(user)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                          <Trash2 size={13} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-10 text-slate-400 text-sm">
                    Kullanıcı bulunamadı
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Yeni Personel Ekle">
        <UserForm onSuccess={() => { setCreateOpen(false); load(); }} onCancel={() => setCreateOpen(false)} />
      </Modal>

      <Modal open={!!editUser} onClose={() => setEditUser(null)} title="Kullanıcıyı Düzenle">
        {editUser && (
          <UserEditForm
            user={editUser}
            branches={branches}
            onSuccess={() => { setEditUser(null); load(); }}
            onCancel={() => setEditUser(null)}
          />
        )}
      </Modal>
    </div>
  );
}
